import { useStore } from '@tanstack/react-store';
import type { SortingState } from '@tanstack/react-table';
import { isLeft, isRight } from 'fp-ts/Either';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import type { TRPC } from '../../contexts/TRPC';
import {
  type HubspotSortableColumnKey,
  hubspotSortableColumnKeys,
} from '../../lib/hubspot-management/columnVisibility';
import { getPipelineStageOptions } from '../../lib/hubspot-management/options';
import {
  HUBSPOT_TICKETS_PAGE_SIZE,
  type HubspotAutomatedReviewSummary,
  type HubspotTicketDetail,
  hubspotManagementStore,
  setLoading as setHubspotLoading,
  setPage as setHubspotPage,
  setPaginationInfo as setHubspotPaginationInfo,
  setSorting as setHubspotSorting,
  setPipelineStages,
  setTickets,
  updateFilter as updateHubspotFilter,
} from '../../stores/hubspotManagementStore';
import { captureAndReportError } from '../../utils/errorReporting';
import { hasAdminAccess } from '../../utils/roleUtils';
import { type AuthState, AuthStateType } from '../useAuth';
import { useExternalSyncStatus } from '../useExternalSyncStatus';
import { useManagementCrud } from '../useManagementCrud';
import type { QueryLoadContext } from '../usePaginatedQueryLoader';

interface HubspotManagementLoadContext {
  filters: {
    pipeline: string;
    requestType: string;
    source: string;
    assignee: string;
    search: string;
  };
  sorting: SortingState;
  pagination: {
    currentPage: number;
    totalPages: number;
    total: number;
  };
}

interface UseHubspotManagementDataOptions {
  authState: AuthState;
  canRender: boolean;
  hasHydratedUrlState: boolean;
  trpcClient: TRPC['client'];
}

export interface HubspotTicketAssignee {
  id: string;
  fullName: string;
}

type HubspotTicketResponse = Omit<
  HubspotTicketDetail,
  'closedDate' | 'createdAt' | 'lastModifiedAt' | 'automatedReviewSummary'
> & {
  closedDate: Date | string | null;
  createdAt: Date | string;
  lastModifiedAt: Date | string;
  automatedReviewSummary:
    | (Omit<HubspotAutomatedReviewSummary, 'lastCompletedAt'> & {
        lastCompletedAt: Date | string | null;
      })
    | null;
};

export function useHubspotManagementData({
  authState,
  canRender,
  hasHydratedUrlState,
  trpcClient,
}: UseHubspotManagementDataOptions) {
  const tickets = useStore(hubspotManagementStore, (state) => state.tickets);
  const selectedTicketId = useStore(
    hubspotManagementStore,
    (state) => state.ui.selectedTicketId,
  );
  const pendingTicketSelection = useStore(
    hubspotManagementStore,
    (state) => state.ui.pendingTicketSelection,
  );
  const pipelineStages = useStore(
    hubspotManagementStore,
    (state) => state.pipelineStages,
  );

  const [assignees, setAssignees] = useState<HubspotTicketAssignee[]>([]);
  const canLoadExternalSyncStatus =
    canRender &&
    authState.type === AuthStateType.Authenticated &&
    hasAdminAccess(authState.user.role, authState.user.email);

  const { syncStatus } = useExternalSyncStatus({
    componentName: 'HubspotManagementPage',
    enabled: canLoadExternalSyncStatus,
  });

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [stagesResult, assigneesResult] = await Promise.all([
          trpcClient.getHubspotPipelineStages.query(undefined),
          trpcClient.getHubspotTicketAssignees.query(undefined),
        ]);

        if (isRight(stagesResult)) {
          setPipelineStages(stagesResult.right as string[]);
        } else if (isLeft(stagesResult)) {
          console.error('Failed to fetch pipeline stages:', stagesResult.left);
        }

        if (isRight(assigneesResult)) {
          setAssignees(assigneesResult.right as HubspotTicketAssignee[]);
        } else if (isLeft(assigneesResult)) {
          console.error('Failed to fetch assignees:', assigneesResult.left);
        }
      } catch (error) {
        console.error('Failed to fetch filter data:', error);
      }
    };

    if (authState.type === AuthStateType.Authenticated && canRender) {
      void fetchFilterData();
    }
  }, [authState.type, canRender, trpcClient]);

  const loadTickets = async ({
    isCurrentRequest,
    filters,
    sorting,
    pagination,
  }: QueryLoadContext & HubspotManagementLoadContext) => {
    const requestedPage = pagination.currentPage;

    try {
      const apiSorting =
        sorting.length > 0
          ? sorting
              .filter((sort) =>
                hubspotSortableColumnKeys.includes(
                  sort.id as HubspotSortableColumnKey,
                ),
              )
              .map((sort) => ({
                id: sort.id as HubspotSortableColumnKey,
                desc: sort.desc,
              }))
          : undefined;

      const result = await trpcClient.listHubspotTickets.query({
        filters: {
          pipelineStage: filters.pipeline || undefined,
          requestType: filters.requestType || undefined,
          submissionSource:
            (filters.source as 'Portal' | 'CLI' | '') || undefined,
          assignedUserId: filters.assignee || undefined,
          search: filters.search || undefined,
        },
        pagination: {
          page: requestedPage,
          limit: HUBSPOT_TICKETS_PAGE_SIZE,
        },
        sorting: apiSorting,
      });

      if (!isCurrentRequest()) {
        return;
      }

      if (isRight(result)) {
        const ticketResponse = result.right as {
          tickets: HubspotTicketResponse[];
          pagination: {
            totalPages: number;
            total: number;
          };
        };
        const convertedTickets: HubspotTicketDetail[] =
          ticketResponse.tickets.map((ticket: HubspotTicketResponse) => ({
            ...ticket,
            closedDate: ticket.closedDate ? new Date(ticket.closedDate) : null,
            createdAt: new Date(ticket.createdAt),
            lastModifiedAt: new Date(ticket.lastModifiedAt),
            automatedReviewSummary: ticket.automatedReviewSummary
              ? {
                  ...ticket.automatedReviewSummary,
                  lastCompletedAt: ticket.automatedReviewSummary.lastCompletedAt
                    ? new Date(ticket.automatedReviewSummary.lastCompletedAt)
                    : null,
                }
              : null,
          }));

        setTickets(convertedTickets);
        setHubspotPaginationInfo(
          requestedPage,
          ticketResponse.pagination.totalPages,
          ticketResponse.pagination.total,
        );
      } else if (isLeft(result)) {
        const error = result.left as { message?: string };
        toast.error(error.message);
      }
    } catch (error) {
      if (!isCurrentRequest()) {
        return;
      }

      captureAndReportError(error, {
        operation: 'admin.loadHubspotTickets',
        component: 'HubspotManagementPage',
        filters: {
          pipelineFilter: filters.pipeline,
          requestTypeFilter: filters.requestType,
          sourceFilter: filters.source,
          searchQuery: filters.search,
        },
        page: requestedPage,
      });
      toast.error('Failed to load HubSpot tickets');
    }
  };

  const {
    filters,
    sorting,
    pagination,
    isLoading,
    reload,
    updateFilter,
    setSorting,
    setPage,
  } = useManagementCrud({
    enabled: canRender && hasHydratedUrlState,
    store: hubspotManagementStore,
    actions: {
      updateFilter: updateHubspotFilter,
      setSorting: setHubspotSorting,
      setPage: setHubspotPage,
      setPaginationInfo: setHubspotPaginationInfo,
      setLoading: setHubspotLoading,
    },
    loadPage: loadTickets,
  });

  return {
    tickets,
    selectedTicketId,
    pendingTicketSelection,
    filters,
    sorting,
    pagination,
    isLoading,
    reloadTickets: reload,
    updateFilter,
    setSorting,
    setPage,
    pipelineStageOptions: getPipelineStageOptions(pipelineStages),
    assigneeOptions: getAssigneeOptions(assignees),
    syncStatus: canLoadExternalSyncStatus ? syncStatus : null,
  };
}

function getAssigneeOptions(
  assignees: HubspotTicketAssignee[],
): Array<{ value: string; label: string }> {
  return [
    { value: '', label: 'All Assignees' },
    { value: '_unassigned', label: 'Unassigned' },
    ...assignees.map((user) => ({
      value: user.id,
      label: user.fullName,
    })),
  ];
}
